import React, { useEffect } from "react";
import { motion } from "framer-motion";
import { 
  Bus, 
  MapPin, 
  Users, 
  BadgeIndianRupee, 
  Car, 
  Compass, 
  ShieldCheck, 
  ChevronRight, 
  Info, 
  Ticket
} from "lucide-react";
import img1 from "../../Assets/img/9.jpg";

const TransportCharges = () => {
  useEffect(() => {
    document.title = "Transport & Other Charges | Khajuraho Routes";
    window.scrollTo(0, 0);
  }, []);

  const guideCharges = [
    { lang: "ENGLISH", pax: "01 - 04 / 05 - 15", rate: "2500 / 3000" },
    { lang: "SPANISH", pax: "01 - 04 / 05 - 15", rate: "2500 / 3500" },
    { lang: "RUSSIAN", pax: "01 - 04 / 05 - 15", rate: "2500 / 3500" },
    { lang: "FRENCH", pax: "01 - 04 / 05 - 15", rate: "2500 / 3500" },
    { lang: "ITALIAN", pax: "01 - 04 / 05 - 15", rate: "2500 / 3500" },
  ];

  const monumentFees = [
    { name: "Western Group", foreigner: "600.00", indian: "40.00" },
    { name: "Light & Sound Show", foreigner: "700.00", indian: "250.00" },
    { name: "Ajay Garh Fort", foreigner: "100.00", indian: "30.00" },
    { name: "Kalinjar Fort", foreigner: "250.00", indian: "100.00" },
    { name: "Orchha Fort", foreigner: "250.00", indian: "30.00" },
    { name: "Panna Jeep Safari (Per Jeep)", foreigner: "6500.00", indian: "3500.00" },
    { name: "Raneh Waterfall", foreigner: "700.00", indian: "700.00" },
    { name: "Pandey Waterfall", foreigner: "400.00", indian: "400.00" },
  ];

  const transportRates = [
    { dest: "GWALIOR - KHAJURAHO", etios: "9,000.00", crysta: "11,000.00", tempo: "18,000.00" },
    { dest: "JHANSI - KHAJURAHO VIA ORCHHA", etios: "7,000.00", crysta: "8,500.00", tempo: "12,000.00" },
    { dest: "KHAJURAHO - BANDHAVGARH", etios: "7,500.00", crysta: "10,000.00", tempo: "12,000.00" },
    { dest: "JABALPUR - BANDHAVGARH", etios: "6,500.00", crysta: "7,500.00", tempo: "12,500.00" },
    { dest: "BANDHAVGARH - KANHA", etios: "8,000.00", crysta: "8,500.00", tempo: "11,500.00" },
    { dest: "KANHA - PENCH", etios: "6,000.00", crysta: "8,500.00", tempo: "11,500.00" },
    { dest: "PENCH - NAGPUR", etios: "3,500.00", crysta: "5,000.00", tempo: "8,500.00" },
    { dest: "KANHA - NAGPUR", etios: "7,000.00", crysta: "10,500.00", tempo: "18,000.00" },
    { dest: "KHAJURAHO - VARANASI", etios: "11,000.00", crysta: "14,000.00", tempo: "31,000.00" },
    { dest: "KHAJURAHO - AGRA", etios: "11,500.00", crysta: "15,000.00", tempo: "22,500.00" },
    { dest: "KANPUR - KHAJURAHO", etios: "7,500.00", crysta: "9,500.00", tempo: "12,500.00" },
    { dest: "ALLAHABAD - KHAJURAHO", etios: "8,500.00", crysta: "11,500.00", tempo: "18,500.00" },
    { dest: "SATNA - KHAJURAHO", etios: "3,000.00", crysta: "4,500.00", tempo: "7,000.00" },
    { dest: "MAHOBA - KHAJURAHO", etios: "3,000.00", crysta: "4,000.00", tempo: "7,500.00" },
    { dest: "LUCKNOW - KHAJURAHO", etios: "9,500.00", crysta: "14,000.00", tempo: "19,500.00" },
    { dest: "JABALPUR - KHAJURAHO", etios: "7,500.00", crysta: "10,500.00", tempo: "14,000.00" },
  ];

  const localRates = [
    { type: "AIRPORT TO HOTEL", etios: "500.00", innova: "1,500.00" },
    { type: "RAILWAY STATION TO HOTEL", etios: "800.00", innova: "800.00" },
    { type: "HALF DAY CITY TOUR", etios: "1,500.00", innova: "1,500.00" },
    { type: "FULL DAY CITY TOUR", etios: "2,200.00", innova: "2,800.00" },
  ];

  return (
    <main className="bg-[#FCFBFA] text-[#1A202C] font-sans selection:bg-orange-100 selection:text-orange-900">
      {/* --- HERO SECTION --- */}
      <section className="relative h-[60vh] flex items-center overflow-hidden bg-[#0A0A0A]">
        <div className="absolute inset-0 z-0">
          <img src={img1} className="w-full h-full object-cover opacity-50 scale-105" alt="Transport" />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-12 w-full">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 1 }}
          >
            <div className="flex items-center gap-3 mb-4">
              <div className="h-[2px] w-12 bg-orange-main" />
              <span className="text-orange-main font-black tracking-[0.4em] text-[10px] uppercase">Tariff Guide</span>
            </div>
            <h1 className="text-3xl md:text-5xl font-black text-white leading-tight uppercase tracking-tight">
              Transport & <span className="text-orange-main">Other Charges</span>
            </h1>
          </motion.div>
        </div>
      </section>

      {/* --- CONTENT SECTION --- */}
      <section className="py-12 bg-[#FDFCFB]">
        <div className="max-w-7xl mx-auto px-2 lg:px-12">
          
          <div className="grid lg:grid-cols-2 gap-8 mb-12">
            {/* Guide Charges */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-main/10 rounded-xl flex items-center justify-center text-orange-main">
                  <Users size={20} />
                </div>
                <h2 className="text-lg font-black uppercase tracking-tight text-gray-900">Guide Charges</h2>
              </div>

              <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
                <table className="w-full border-collapse table-fixed">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[11px] uppercase tracking-wider font-black text-left text-gray-600 w-[40%]">Language</th>
                      <th className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[11px] uppercase tracking-wider font-black text-center text-gray-600 w-[30%]">Pax</th>
                      <th className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[11px] uppercase tracking-wider font-black text-right text-gray-600 w-[30%]">Rate (INR)</th>
                    </tr>
                  </thead>
                  <tbody>
                    {guideCharges.map((item, i) => (
                      <tr key={i} className="hover:bg-orange-50/30 transition-colors">
                        <td className="border border-gray-300 px-3 py-4 text-[10px] lg:text-[13px] font-bold text-gray-800">{item.lang}</td>
                        <td className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[12px] font-medium text-gray-500 text-center">{item.pax}</td>
                        <td className="border border-gray-300 px-3 py-4 text-[10px] lg:text-[13px] font-black text-orange-main text-right">{item.rate}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>

            {/* Monument Fees */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-orange-main/10 rounded-xl flex items-center justify-center text-orange-main">
                  <Ticket size={20} />
                </div>
                <h2 className="text-lg font-black uppercase tracking-tight text-gray-900">Entrance Fees</h2>
              </div>

              <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
                <table className="w-full border-collapse table-fixed">
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[11px] uppercase tracking-wider font-black text-left text-gray-600 w-[40%]">Monument</th>
                      <th className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[11px] uppercase tracking-wider font-black text-center text-gray-600 w-[30%]">Foreigner</th>
                      <th className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[11px] uppercase tracking-wider font-black text-right text-gray-600 w-[30%]">Indian</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monumentFees.map((item, i) => (
                      <tr key={i} className="hover:bg-orange-50/30 transition-colors">
                        <td className="border border-gray-300 px-3 py-4 text-[10px] lg:text-[13px] font-bold text-gray-800 break-words">{item.name}</td>
                        <td className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[12px] font-medium text-gray-500 text-center">₹ {item.foreigner}</td>
                        <td className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[12px] font-medium text-gray-500 text-right">₹ {item.indian}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </motion.div>
          </div>

          {/* Transport Rates */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-orange-main/10 rounded-2xl flex items-center justify-center text-orange-main">
                <Car size={24} />
              </div>
              <h2 className="text-xl font-black uppercase tracking-tight text-gray-900">Inter-City Transfers</h2>
            </div>

            <div className="overflow-hidden rounded-2xl border border-gray-300 bg-white shadow-lg">
              <table className="w-full border-collapse table-fixed">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border border-gray-300 px-2 lg:px-8 py-5 text-[8px] lg:text-[11px] uppercase tracking-tighter font-black text-left text-gray-600 w-[38%]">Route</th>
                    <th className="border border-gray-300 px-1 lg:px-6 py-5 text-[8px] lg:text-[11px] uppercase tracking-tighter font-black text-center text-gray-600">Etios</th>
                    <th className="border border-gray-300 px-1 lg:px-6 py-5 text-[8px] lg:text-[11px] uppercase tracking-tighter font-black text-center text-gray-600">Crysta</th>
                    <th className="border border-gray-300 px-2 lg:px-8 py-5 text-[8px] lg:text-[11px] uppercase tracking-tighter font-black text-right text-gray-600 w-[18%]">Tempo</th>
                  </tr>
                </thead>
                <tbody>
                  {transportRates.map((item, i) => (
                    <tr key={i} className={`${i % 2 === 0 ? "bg-white" : "bg-gray-50/50"} hover:bg-orange-50/50 transition-all`}>
                      <td className="border border-gray-300 px-2 lg:px-8 py-4 text-[9px] lg:text-[14px] font-bold text-gray-800 break-words">{item.dest}</td>
                      <td className="border border-gray-300 px-1 lg:px-6 py-4 text-[8px] lg:text-[13px] font-medium text-gray-500 text-center">₹ {item.etios}</td>
                      <td className="border border-gray-300 px-1 lg:px-6 py-4 text-[8px] lg:text-[13px] font-medium text-gray-500 text-center">₹ {item.crysta}</td>
                      <td className="border border-gray-300 px-2 lg:px-8 py-4 text-[9px] lg:text-[14px] font-bold text-orange-main text-right">₹ {item.tempo}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Note Box */}
            <div className="mt-8 p-6 bg-gray-900 rounded-3xl text-white relative overflow-hidden shadow-xl">
              <div className="relative z-10 flex flex-col md:flex-row justify-between items-center gap-6">
                <div className="space-y-2 text-center md:text-left">
                  <h3 className="text-lg font-black uppercase tracking-tight text-orange-main">Terms & Inclusions</h3>
                  <p className="text-gray-400 text-[10px] md:text-xs font-bold leading-relaxed">
                    GST 5% extra. Includes Toll Tax, Parking, Fuel & Driver Allowance.
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <ShieldCheck size={24} className="text-orange-main" />
                  <span className="text-[10px] uppercase tracking-widest text-gray-300 font-black">All Inclusive Pricing</span>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Local Transfer Rates */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="space-y-4"
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-orange-main/10 rounded-xl flex items-center justify-center text-orange-main">
                <MapPin size={20} />
              </div>
              <h2 className="text-lg font-black uppercase tracking-tight text-gray-900">Local Transfers</h2>
            </div>

            <div className="overflow-hidden rounded-xl border border-gray-300 bg-white shadow-sm">
              <table className="w-full border-collapse table-fixed">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[11px] uppercase tracking-wider font-black text-left text-gray-600 w-[40%]">Service Type</th>
                    <th className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[11px] uppercase tracking-wider font-black text-center text-gray-600">Etios</th>
                    <th className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[11px] uppercase tracking-wider font-black text-right text-gray-600">Innova</th>
                  </tr>
                </thead>
                <tbody>
                  {localRates.map((item, i) => (
                    <tr key={i} className="hover:bg-orange-50/30 transition-colors">
                      <td className="border border-gray-300 px-3 py-4 text-[10px] lg:text-[13px] font-bold text-gray-800 break-words">{item.type}</td>
                      <td className="border border-gray-300 px-3 py-4 text-[9px] lg:text-[12px] font-medium text-gray-500 text-center">₹ {item.etios}</td>
                      <td className="border border-gray-300 px-3 py-4 text-[10px] lg:text-[13px] font-bold text-orange-main text-right">₹ {item.innova}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </motion.div>

        </div>
      </section>
    </main>
  );
};

export default TransportCharges;
